import { createSlice } from '@reduxjs/toolkit';

/**
 * Redux slice for managing chapters and topics data
 *
 * This slice handles:
 * - Fetching and caching chapters based on country, board, grade, and subject
 * - Fetching and caching topics based on selected chapters
 * - Managing loading and error states for both
 * - Optimistic updates to prevent UI flickering
 * - Cache management with TTL support
 *
 * The data is cached to avoid redundant API calls when the same parameters are used.
 */

// Cache TTL in milliseconds (5 minutes)
const CACHE_TTL = 5 * 60 * 1000;

const initialState = {
  // Chapters data and state
  chapters: [],
  chaptersLoading: false,
  chaptersError: null,
  chaptersParams: null, // Stores the params used to fetch current chapters (for cache validation)
  chaptersPreviousData: [], // Keep previous data during loading for smooth transitions

  // Topics data and state
  topics: [],
  topicsLoading: false,
  topicsError: null,
  topicsParams: null, // Stores the params used to fetch current topics (for cache validation)
  topicsPreviousData: [], // Keep previous data during loading for smooth transitions

  // Last fetch timestamps for cache invalidation
  chaptersLastFetch: null,
  topicsLastFetch: null,

  // Cache storage for quick lookups (keyed by params hash)
  chaptersCache: {},
  topicsCache: {},

  // Initialization state
  isInitialized: false,
};

const chaptersTopicsSlice = createSlice({
  name: 'chaptersTopics',
  initialState,
  reducers: {
    // Chapters actions
    fetchChaptersStart: (state) => {
      // Store previous data for smooth transitions (optimistic UI)
      state.chaptersPreviousData = state.chapters;
      state.chaptersLoading = true;
      state.chaptersError = null;
    },
    fetchChaptersSuccess: (state, action) => {
      const { chapters, params, cacheKey } = action.payload;
      state.chaptersLoading = false;
      state.chapters = chapters;
      state.chaptersParams = params;
      state.chaptersLastFetch = Date.now();
      state.chaptersError = null;
      state.chaptersPreviousData = [];
      state.isInitialized = true;

      // Store in cache
      if (cacheKey) {
        state.chaptersCache[cacheKey] = {
          data: chapters,
          params,
          timestamp: Date.now(),
        };
      }
    },
    fetchChaptersFailure: (state, action) => {
      state.chaptersLoading = false;
      state.chaptersError = action.payload;
      // Keep previous data on error for better UX
      if (state.chaptersPreviousData.length > 0) {
        state.chapters = state.chaptersPreviousData;
      }
      state.chaptersPreviousData = [];
    },
    setChaptersFromCache: (state, action) => {
      const { chapters, params } = action.payload;
      state.chapters = chapters;
      state.chaptersParams = params;
      state.chaptersLoading = false;
      state.chaptersError = null;
      state.isInitialized = true;
    },
    clearChapters: (state) => {
      state.chapters = [];
      state.chaptersParams = null;
      state.chaptersError = null;
      state.chaptersPreviousData = [];
    },

    // Topics actions
    fetchTopicsStart: (state) => {
      // Store previous data for smooth transitions (optimistic UI)
      state.topicsPreviousData = state.topics;
      state.topicsLoading = true;
      state.topicsError = null;
    },
    fetchTopicsSuccess: (state, action) => {
      const { topics, params, cacheKey } = action.payload;
      state.topicsLoading = false;
      state.topics = topics;
      state.topicsParams = params;
      state.topicsLastFetch = Date.now();
      state.topicsError = null;
      state.topicsPreviousData = [];

      // Store in cache
      if (cacheKey) {
        state.topicsCache[cacheKey] = {
          data: topics,
          params,
          timestamp: Date.now(),
        };
      }
    },
    fetchTopicsFailure: (state, action) => {
      state.topicsLoading = false;
      state.topicsError = action.payload;
      // Keep previous data on error for better UX
      if (state.topicsPreviousData.length > 0) {
        state.topics = state.topicsPreviousData;
      }
      state.topicsPreviousData = [];
    },
    setTopicsFromCache: (state, action) => {
      const { topics, params } = action.payload;
      state.topics = topics;
      state.topicsParams = params;
      state.topicsLoading = false;
      state.topicsError = null;
    },
    clearTopics: (state) => {
      state.topics = [];
      state.topicsParams = null;
      state.topicsError = null;
      state.topicsPreviousData = [];
    },

    // Reset all data
    resetChaptersTopics: (_state) => {
      return { ...initialState };
    },

    // Clear expired cache entries
    clearExpiredCache: (state) => {
      const now = Date.now();

      // Clear expired chapters cache
      Object.keys(state.chaptersCache).forEach(key => {
        if (now - state.chaptersCache[key].timestamp > CACHE_TTL) {
          delete state.chaptersCache[key];
        }
      });

      // Clear expired topics cache
      Object.keys(state.topicsCache).forEach(key => {
        if (now - state.topicsCache[key].timestamp > CACHE_TTL) {
          delete state.topicsCache[key];
        }
      });
    },
  },
});

export const {
  fetchChaptersStart,
  fetchChaptersSuccess,
  fetchChaptersFailure,
  setChaptersFromCache,
  clearChapters,
  fetchTopicsStart,
  fetchTopicsSuccess,
  fetchTopicsFailure,
  setTopicsFromCache,
  clearTopics,
  resetChaptersTopics,
  clearExpiredCache,
} = chaptersTopicsSlice.actions;

// Selectors for easy access to state
export const selectChapters = (state) => state.chaptersTopics.chapters;
export const selectChaptersLoading = (state) => state.chaptersTopics.chaptersLoading;
export const selectChaptersError = (state) => state.chaptersTopics.chaptersError;
export const selectChaptersParams = (state) => state.chaptersTopics.chaptersParams;
export const selectChaptersPreviousData = (state) => state.chaptersTopics.chaptersPreviousData;
export const selectChaptersCache = (state) => state.chaptersTopics.chaptersCache;

export const selectTopics = (state) => state.chaptersTopics.topics;
export const selectTopicsLoading = (state) => state.chaptersTopics.topicsLoading;
export const selectTopicsError = (state) => state.chaptersTopics.topicsError;
export const selectTopicsParams = (state) => state.chaptersTopics.topicsParams;
export const selectTopicsPreviousData = (state) => state.chaptersTopics.topicsPreviousData;
export const selectTopicsCache = (state) => state.chaptersTopics.topicsCache;

export const selectIsInitialized = (state) => state.chaptersTopics.isInitialized;

// Helper to check if cache is valid
export const isCacheValid = (cacheEntry) => {
  if (!cacheEntry) return false;
  return Date.now() - cacheEntry.timestamp < CACHE_TTL;
};

// Export cache TTL for actions
export { CACHE_TTL };

export default chaptersTopicsSlice.reducer;
